/**
 * The store's published policies, and the rule for when a click on one should open
 * the checkout's popup instead of navigating.
 *
 * Shared because THREE places in the funnel link to a policy — the layout's footer
 * (CheckoutLayout, shown on /upsell and /order/:token), the row under the pay button
 * and the "by paying you agree to…" line (both CheckoutRoot, and the layout's footer
 * is hidden on /checkout precisely so they are not duplicated). Each owns its own
 * modal instance, which is fine because only one nav is ever clicked; what must NOT
 * differ between them is which policies exist and what counts as a plain click.
 */

/** A policy, with the body the popup renders. `key` is the /policies/:handle segment. */
export type Policy = { key: string; title: string; body: string }

/**
 * Every policy the store has published, in the shape the popup needs.
 *
 * The body is already in the bootstrap payload — it is what PolicyPage reads — so
 * showing one costs no request. A policy the seller has titled and not written is
 * kept rather than filtered out: it is still a link the buyer expects to see, and
 * the popup says it is unpublished, which is what /policies/:handle already does.
 */
export function policyList(settings: Record<string, Record<string, unknown>>): Policy[] {
  const p = (settings.policies ?? {}) as Record<string, { title?: string; body?: string }>
  return Object.entries(p).map(([key, v]) => ({ key, title: v?.title || key, body: v?.body || "" }))
}

/**
 * Whether this click is the browser's to handle.
 *
 * Every policy link stays a real `<a href>`, so middle-click and cmd/ctrl-click
 * still open the full /policies page in a NEW tab — a buyer who wants the refund
 * policy open beside the form should not lose that just because we added a modal.
 * Only an unmodified primary click is intercepted, which is also what makes the
 * keyboard's Enter (no modifiers, button 0) open the popup.
 */
export function plainClick(e: MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
}
